import React, { useState, useEffect, Fragment } from "react";
import InvoiceModal from "./NewSales/components/InvoiceModal";
import { buildInvoiceFromSale } from "./utils/buildInvoiceFromSale";
import { Button, Card, Col, Table, Input } from "reactstrap";
import { FormLabel } from "react-bootstrap";
import { Download, Filter, X } from "react-feather";
import Skeleton from "react-loading-skeleton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { productSalesHistoryList, getPresPreSignedUrl, getSaleInfo } from "./SalesService";
import { useAtom } from "jotai";
import {
	ProductSaleHistoryPageAtom,
	ProductSaleHistoryPageSizeAtom,
} from "./SalesAtoms";
import { useCurrency } from "../../_helper/CurrencyContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import {
	buildProductSalesHistoryRequest,
	formatAmount,
	formatListDate,
	getBrandAndSalt,
	getDoctorName,
	getPatientContact,
	getPharmacyDetails,
	canDownloadPrescription,
	resolvePrescriptionPresignedUrl,
	getSaleAmount,
	getSaleCurrency,
	getSaleStatusLabel,
	parseProductSalesHistoryResponse,
} from "./utils/productSalesHistoryUtils";

const PatientCell = ({ sale }) => {
	const { name, email, phone } = getPatientContact(sale);

	return (
		<div>
			<div>{name}</div>
			{email ? (
				<div className="text-muted small">
					<i className="fa fa-envelope-o me-1" aria-hidden="true" />
					{email}
				</div>
			) : null}
			{phone ? (
				<div className="text-muted small">
					<i className="fa fa-phone me-1" aria-hidden="true" />
					{phone}
				</div>
			) : null}
		</div>
	);
};

const PRODUCT_CELL_WIDTH = 220;

const ellipsisStyle = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	display: "block",
	maxWidth: PRODUCT_CELL_WIDTH,
};

const ProductCell = ({ sale, rowKey, activeTooltipId, setActiveTooltipId }) => {
	const { t } = useTranslation();
	const { brand, salt } = getBrandAndSalt(sale);
	const brandTipId = `psh-brand-${rowKey}`;
	const saltTipId = `psh-salt-${rowKey}`;

	return (
		<div style={{ width: PRODUCT_CELL_WIDTH, maxWidth: PRODUCT_CELL_WIDTH }}>
			<div
				id={brandTipId}
				style={ellipsisStyle}
				onMouseEnter={() => setActiveTooltipId(brandTipId)}
				onMouseLeave={() =>
					setActiveTooltipId((prev) => (prev === brandTipId ? null : prev))
				}
			>
				{brand}
			</div>
			<ToolTip
				attrToolTip={{
					placement: "top",
					isOpen: activeTooltipId === brandTipId,
					target: brandTipId,
					toggle: () =>
						setActiveTooltipId((prev) =>
							prev === brandTipId ? null : brandTipId
						),
				}}
			>
				<strong>{t("Brand Name")}: </strong>
				{brand}
			</ToolTip>
			{salt ? (
				<>
					<div
						id={saltTipId}
						className="text-muted small"
						style={ellipsisStyle}
						onMouseEnter={() => setActiveTooltipId(saltTipId)}
						onMouseLeave={() =>
							setActiveTooltipId((prev) => (prev === saltTipId ? null : prev))
						}
					>
						{salt}
					</div>
					<ToolTip
						attrToolTip={{
							placement: "top",
							isOpen: activeTooltipId === saltTipId,
							target: saltTipId,
							toggle: () =>
								setActiveTooltipId((prev) =>
									prev === saltTipId ? null : saltTipId
								),
						}}
					>
						<strong>{t("Salt / Product Name")}: </strong>
						{salt}
					</ToolTip>
				</>
			) : null}
		</div>
	);
};

const ProductSalesHistory = () => {
	const { getCurrencySymbol } = useCurrency();
	const { pharmacyPOS } = usePharmacy();
	const [salesData, setSalesData] = useState([]);
	const [page, setPage] = useAtom(ProductSaleHistoryPageAtom);
	const [size, setSize] = useAtom(ProductSaleHistoryPageSizeAtom);
	const [totalItems, setTotalItems] = useState(0);
	const [searchValue, setSearchValue] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [appliedFilters, setAppliedFilters] = useState({
		search: "",
		startDate: "",
		endDate: "",
	});
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [actionTooltipId, setActionTooltipId] = useState(null);
	const [productTooltipId, setProductTooltipId] = useState(null);
	const [showPosInvoice, setShowPosInvoice] = useState(false);
	const [posInvoiceData, setPosInvoiceData] = useState(null);
	const [loadingPosReceiptId, setLoadingPosReceiptId] = useState(null);
	const { t } = useTranslation();

	const tableColumnCount = 9;

	useEffect(() => {
		getProductSalesList();
	}, [page, size, appliedFilters]);

	const getProductSalesList = async () => {
		const body = buildProductSalesHistoryRequest({
			page,
			limit: size,
			...appliedFilters,
		});

		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await productSalesHistoryList(body);
			const { list, total } = parseProductSalesHistoryResponse(response);

			setSalesData(list);
			setTotalItems(Math.ceil(total / size) || 0);
		} catch (error) {
			console.error("Error fetching product sales history:", error);
			setSalesData([]);
			setTotalItems(0);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const handleSearchButtonClick = () => {
		setPage(1);
		setAppliedFilters({
			search: searchValue,
			startDate,
			endDate,
		});
	};

	const handleClear = () => {
		setSearchValue("");
		setStartDate("");
		setEndDate("");
		setPage(1);
		setAppliedFilters({ search: "", startDate: "", endDate: "" });
	};

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseFloat(e.target.value));
		setPage(1);
	};

	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);

	const printPosInvoice = () => {
		const invoiceContent = document?.getElementById("invoice-content");
		if (!invoiceContent) return;
		const printWindow = window.open("", "", "width=400,height=600");
		if (!printWindow) return;
		printWindow.document.write(`
			<html>
				<head>
					<title>Invoice</title>
					<style>
						@page { size: 80mm auto; margin: 5mm; }
						body { font-family: Arial, sans-serif; font-size: 12px; width: 80mm; margin: 0 auto; }
						.text-center { text-align: center; }
						.text-end { text-align: right; }
						.bold { font-weight: bold; }
						.dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
						table { width: 100%; border-collapse: collapse; font-size: 12px; }
						td { padding: 2px 0; vertical-align: top; }
						.section { margin-top: 6px; margin-bottom: 6px; }
						.thankyou { margin-top: 12px; font-size: 12px; text-align: center; }
					</style>
				</head>
				<body>${invoiceContent.innerHTML}</body>
			</html>
		`);
		printWindow.document.close();
		printWindow.focus();
		setTimeout(() => {
			printWindow.print();
			printWindow.close();
		}, 400);
	};

	const handlePosReceipt = async (sale) => {
		const salesId = sale.salesId;
		if (!salesId) {
			toast.warning(t("Sale details not available."));
			return;
		}

		try {
			setLoadingPosReceiptId(salesId);
			setLoading(true);

			const response = await getSaleInfo({
				clinicId: pharmacyPOS?.hospital_id,
				salesId,
			});

			if (response.data?.status !== "success") {
				toast.warning(t("Failed to load sale details."));
				return;
			}

			const invoice = buildInvoiceFromSale(response.data.data, getCurrencySymbol);
			if (!invoice) {
				toast.warning(t("Failed to generate invoice."));
				return;
			}

			setPosInvoiceData(invoice);
			setShowPosInvoice(true);
		} catch (error) {
			console.error("Error loading POS receipt:", error);
			toast.warning(t("Failed to load POS receipt."));
		} finally {
			setLoadingPosReceiptId(null);
			setLoading(false);
		}
	};

	const handleDownloadPrescription = async (sale) => {
		try {
			setLoading(true);

			const url = await resolvePrescriptionPresignedUrl(sale, {
				getSaleInfo,
				getPresPreSignedUrl,
				clinicId: pharmacyPOS?.hospital_id,
			});

			if (url) {
				window.open(url, "_blank");
			} else {
				toast.warning(t("Prescription not available."));
			}
		} catch (error) {
			console.error("Error downloading prescription:", error);
			toast.warning(t("Failed to download prescription."));
		} finally {
			setLoading(false);
		}
	};

	const getStatusBadge = (sale) => {
		if (sale.isSettled) {
			return <span className="badge bg-success">{t("Settled")}</span>;
		}
		if (sale.isPaid) {
			return <span className="badge bg-primary">{t("Paid")}</span>;
		}
		if (sale.isPartialPaid) {
			return <span className="badge bg-warning">{t("Partial")}</span>;
		}
		if (sale.dueAmount > 0) {
			return <span className="badge bg-danger">{t("Pending")}</span>;
		}
		if (sale.isReturn || sale.salesType === "return") {
			return <span className="badge bg-danger">{t("Return")}</span>;
		}
		if (sale.billing?.payment?.status) {
			return (
				<span className="badge bg-secondary text-capitalize">
					{sale.billing.payment.status}
				</span>
			);
		}
		return null;
	};

	const handleExportToExcel = async () => {
		try {
			setIsExporting(true);
			setLoading(true);

			const body = buildProductSalesHistoryRequest({
				page: 1,
				limit: "",
				...appliedFilters,
			});

			const response = await productSalesHistoryList(body);
			const { list } = parseProductSalesHistoryResponse(response);

			if (!list.length) {
				toast.warning(t("No product sales records to export."));
				return;
			}

			const pharmacy = getPharmacyDetails(pharmacyPOS);
			const prescriptionUrlCache = new Map();
			const saleInfoCache = new Map();
			const prescriptionResolver = {
				getSaleInfo,
				getPresPreSignedUrl,
				clinicId: pharmacyPOS?.hospital_id,
			};

			const tableHeaders = [
				"#",
				t("Sale Number"),
				t("Selling Date"),
				t("Patient"),
				t("Patient Email"),
				t("Patient Phone"),
				t("Doctor Name"),
				t("Brand Name"),
				t("Salt / Product Name"),
				t("Amount"),
				t("Status"),
				t("Prescription URL"),
			];

			const tableRows = await Promise.all(
				list.map(async (sale, index) => {
					const currency = getSaleCurrency(sale, pharmacyPOS);
					const patient = getPatientContact(sale);
					const { brand, salt } = getBrandAndSalt(sale);
					const prescriptionUrl = await resolvePrescriptionPresignedUrl(
						sale,
						prescriptionResolver,
						prescriptionUrlCache,
						saleInfoCache
					);

					return [
						index + 1,
						sale.salesNumber || "-",
						formatListDate(sale.createdAt),
						patient.name,
						patient.email || "-",
						patient.phone || "-",
						getDoctorName(sale),
						brand,
						salt || "-",
						`${getCurrencySymbol(currency)}${formatAmount(getSaleAmount(sale))}`,
						getSaleStatusLabel(sale, t),
						prescriptionUrl || "-",
					];
				})
			);

			const filterRows = [];
			if (appliedFilters.search) {
				filterRows.push([t("Search"), appliedFilters.search]);
			}
			if (appliedFilters.startDate || appliedFilters.endDate) {
				filterRows.push([
					t("Date Range"),
					`${appliedFilters.startDate || "—"} ${t("to")} ${appliedFilters.endDate || "—"}`,
				]);
			}

			const worksheetData = [
				[t("Product Sales History")],
				[],
				[t("Pharmacy Name"), pharmacy.name],
				[t("Email"), pharmacy.email],
				[t("Address"), pharmacy.address],
				[],
				...filterRows,
				...(filterRows.length ? [[]] : []),
				[t("Generated On"), new Date().toLocaleString()],
				[],
				tableHeaders,
				...tableRows,
			];

			const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
			worksheet["!cols"] = [
				{ wch: 6 },
				{ wch: 14 },
				{ wch: 16 },
				{ wch: 22 },
				{ wch: 26 },
				{ wch: 18 },
				{ wch: 20 },
				{ wch: 24 },
				{ wch: 36 },
				{ wch: 14 },
				{ wch: 12 },
				{ wch: 50 },
			];

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Product Sales");

			const excelBuffer = XLSX.write(workbook, {
				bookType: "xlsx",
				type: "array",
			});
			const blob = new Blob([excelBuffer], {
				type: "application/octet-stream",
			});

			const fileDate = new Date().toISOString().split("T")[0];
			saveAs(blob, `product_sales_history_${fileDate}.xlsx`);
			toast.success(t("Product sales history exported successfully."));
		} catch (error) {
			console.error("Error exporting product sales history:", error);
			toast.warning(t("Failed to export product sales history."));
		} finally {
			setIsExporting(false);
			setLoading(false);
		}
	};

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Product Sales History")}
				parent={t("Dashboard")}
				title={t("Product Sales History")}
			/>
			<Col sm="12">
				<Card>
					<div
						className="row"
						style={{ alignItems: "center", padding: "12px 24px" }}
					>
						<Col xs="12" className="d-flex justify-content-end mb-2 px-0">
							<Button
								color="primary"
								outline
								className="d-flex align-items-center gap-2"
								onClick={handleExportToExcel}
								disabled={isExporting}
							>
								<Download size={16} />
								{t("Export to Excel")}
							</Button>
						</Col>
						<Col sm="12" lg="4" xl="4" md="5">
							<Input
								type="text"
								placeholder={t(
									"Search by brand, product, doctor, email or phone"
								)}
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSearchButtonClick();
								}}
							/>
						</Col>
						<Col sm="6" lg="2" xl="2" md="2" className="mt-2 mt-md-0">
							<Input
								type="date"
								value={startDate}
								max={endDate || new Date().toISOString().split("T")[0]}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</Col>
						<Col sm="6" lg="2" xl="2" md="2" className="mt-2 mt-md-0">
							<Input
								type="date"
								value={endDate}
								min={startDate || undefined}
								max={new Date().toISOString().split("T")[0]}
								onChange={(e) => setEndDate(e.target.value)}
							/>
						</Col>
						<Col
							className="col-xl-4 col-lg-4 col-md-5 col-sm-12 d-flex justify-content-start align-items-center mt-2 mt-md-0"
							style={{ gap: "18px" }}
						>
							<button
								id="ProductSalesFilterToolTip"
								className="btn btn-primary"
								style={{ height: "40px" }}
								onClick={handleSearchButtonClick}
							>
								<Filter />
							</button>
							<ToolTip
								attrToolTip={{
									placement: "top",
									isOpen: filterTooltipOpen,
									target: "ProductSalesFilterToolTip",
									toggle: toggleFilterTooltip,
								}}
							>
								<strong>{t("Filter")}</strong>
							</ToolTip>
							<button
								id="ProductSalesClearToolTip"
								className="btn btn-secondary"
								style={{ height: "40px" }}
								onClick={handleClear}
							>
								<X />
							</button>
							<ToolTip
								attrToolTip={{
									placement: "top",
									isOpen: clearTooltipOpen,
									target: "ProductSalesClearToolTip",
									toggle: toggleClearTooltip,
								}}
							>
								<strong>{t("Clear")}</strong>
							</ToolTip>
						</Col>
					</div>
				</Card>
				<Card>
					<div className="card-block">
						<div className="row" style={{ padding: "12px 24px" }}>
							<Col sm="12">
								<div className="rounded table-responsive">
									<Table className="mb-0 table-center table-bordered table-hover">
										<thead>
											<tr>
												<th>#</th>
												<th>{t("Sale Number")}</th>
												<th>{t("Selling Date")}</th>
												<th>{t("Patient")}</th>
												<th>{t("Doctor Name")}</th>
												<th style={{ width: PRODUCT_CELL_WIDTH, maxWidth: PRODUCT_CELL_WIDTH }}>
													{t("Brand/Product Name")}
												</th>
												<th>{t("Amount")}</th>
												<th>{t("Status")}</th>
												<th className="text-center">{t("Action")}</th>
											</tr>
										</thead>
										<tbody>
											{skeletonLoading ? (
												[...Array(5)].map((_, i) => (
													<tr key={i}>
														{Array(tableColumnCount)
															.fill()
															.map((_, idx) => (
																<td key={idx}>
																	<Skeleton height={20} />
																</td>
															))}
													</tr>
												))
											) : salesData.length > 0 ? (
												salesData.map((sale, index) => {
													const currency = getSaleCurrency(
														sale,
														pharmacyPOS
													);
													const rowKey = sale._id || index;
													const posReceiptTipId = `psh-pos-receipt-${rowKey}`;
													const prescriptionTipId = `psh-prescription-${rowKey}`;
													const showPrescriptionAction =
														canDownloadPrescription(sale);
													const showPosReceiptAction = Boolean(sale.salesId);

													return (
														<tr
															key={
																sale._id ||
																`${sale.salesNumber}-${index}`
															}
														>
															<td>{(page - 1) * size + index + 1}</td>
															<td>
																{sale.salesNumber || "-"}
																{sale.isReturn && (
																	<span className="badge bg-danger ms-2">
																		{t("Returned")}
																	</span>
																)}
															</td>
															<td>{formatListDate(sale.createdAt)}</td>
															<td>
																<PatientCell sale={sale} />
															</td>
															<td>{getDoctorName(sale)}</td>
															<td
																style={{
																	width: PRODUCT_CELL_WIDTH,
																	maxWidth: PRODUCT_CELL_WIDTH,
																}}
															>
																<ProductCell
																	sale={sale}
																	rowKey={sale._id || index}
																	activeTooltipId={productTooltipId}
																	setActiveTooltipId={setProductTooltipId}
																/>
															</td>
															<td>
																<strong>
																	{getCurrencySymbol(currency)}
																	{formatAmount(getSaleAmount(sale))}
																</strong>
															</td>
															<td>{getStatusBadge(sale)}</td>
															<td className="text-center">
																{showPosReceiptAction || showPrescriptionAction ? (
																	<div className="d-flex justify-content-center align-items-center gap-1">
																		{showPosReceiptAction ? (
																			<>
																				<button
																					type="button"
																					className="success-action-button"
																					id={posReceiptTipId}
																					disabled={
																						loadingPosReceiptId ===
																						sale.salesId
																					}
																					onClick={() =>
																						handlePosReceipt(sale)
																					}
																				>
																					<i
																						className="fa fa-file-text-o"
																						style={{ fontSize: "18px" }}
																					/>
																				</button>
																				<ToolTip
																					attrToolTip={{
																						placement: "top",
																						isOpen:
																							actionTooltipId ===
																							posReceiptTipId,
																						target: posReceiptTipId,
																						toggle: () =>
																							setActionTooltipId((prev) =>
																								prev === posReceiptTipId
																									? null
																									: posReceiptTipId
																							),
																					}}
																				>
																					<strong>{t("POS Receipt")}</strong>
																				</ToolTip>
																			</>
																		) : null}
																		{showPrescriptionAction ? (
																			<>
																				<button
																					type="button"
																					className="success-action-button"
																					id={prescriptionTipId}
																					onClick={() =>
																						handleDownloadPrescription(sale)
																					}
																				>
																					<i
																						className="fa fa-external-link"
																						style={{ fontSize: "18px" }}
																					/>
																				</button>
																				<ToolTip
																					attrToolTip={{
																						placement: "top",
																						isOpen:
																							actionTooltipId ===
																							prescriptionTipId,
																						target: prescriptionTipId,
																						toggle: () =>
																							setActionTooltipId((prev) =>
																								prev === prescriptionTipId
																									? null
																									: prescriptionTipId
																							),
																					}}
																				>
																					<strong>
																						{t("View Prescription")}
																					</strong>
																				</ToolTip>
																			</>
																		) : null}
																	</div>
																) : (
																	<span className="text-muted">-</span>
																)}
															</td>
														</tr>
													);
												})
											) : (
												<tr className="text-center">
													<td colSpan={tableColumnCount}>
														{t("No product sales records available.")}
													</td>
												</tr>
											)}
										</tbody>
									</Table>
								</div>
							</Col>

							<Col sm="12" className="mt-4">
								<div className="text-center d-md-flex align-items-center justify-content-between">
									<div className="pt-3 show-entries">
										<FormLabel>{t("Show")}&nbsp;</FormLabel>
										<select
											value={size}
											onChange={handlePageSizeChange}
											className="form-control"
											style={{ display: "unset", width: "auto" }}
										>
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={20}>20</option>
											<option value={30}>30</option>
											<option value={50}>50</option>
										</select>
										&nbsp;
										<FormLabel>{t("entries")}</FormLabel>
									</div>
									<MyPagination
										totalPages={totalItems}
										currentPage={page}
										onPageChange={handlePageChange}
									/>
								</div>
							</Col>
						</div>
					</div>
				</Card>
			</Col>
			<InvoiceModal
				t={t}
				show={showPosInvoice}
				invoiceData={posInvoiceData}
				pharmacyData={pharmacyPOS}
				onHide={() => setShowPosInvoice(false)}
				onClose={() => setShowPosInvoice(false)}
				onPrint={printPosInvoice}
			/>
		</Fragment>
	);
};

export default ProductSalesHistory;
